import { z } from 'zod';

// ── Persona reference vocabulary ────────────────────────────────────────────
// Controlled vocabularies for the maintained buyer-persona taxonomy (the
// `personas` reference table). A Company Profile selects personas from this set
// so downstream agents (e.g. the Lead Qualifier) can match `wrong_persona`
// deterministically instead of fuzzy-guessing. New in this work — no persona
// concept existed before. Mirrors the controlled-vocab pattern of
// `EMPLOYEE_BANDS`/`FUNDING_STAGES` in `companies.ts`.

export const PERSONA_DEPARTMENTS = [
  'Revenue Operations',
  'Sales',
  'Marketing',
  'Customer Success',
  'Data & Analytics',
  'Engineering',
  'Product',
  'Operations',
  'Privacy & Legal',
  'Compliance & Risk',
  'Security',
  'Finance',
  'People & HR',
  'Executive',
  'IT',
] as const;
export type PersonaDepartment = (typeof PERSONA_DEPARTMENTS)[number];

export const PERSONA_SENIORITIES = [
  'IC',
  'Manager',
  'Director',
  'VP',
  'C-Level',
  'Founder',
] as const;
export type PersonaSeniority = (typeof PERSONA_SENIORITIES)[number];

export const PERSONA_BUYER_ROLES = [
  'economic_buyer',
  'champion',
  'influencer',
  'user',
  'blocker',
] as const;
export type PersonaBuyerRole = (typeof PERSONA_BUYER_ROLES)[number];

/** A buyer-persona archetype (a row in the `personas` reference table). */
export type Persona = {
  /** Stable slug, e.g. 'revops_leader'. */
  key: string;
  /** Canonical label, e.g. 'VP of Revenue Operations'. */
  name: string;
  department: PersonaDepartment;
  seniority: PersonaSeniority;
  buyerRole: PersonaBuyerRole;
  /** Title variants for deterministic matching ('Rev Ops', 'Head of RevOps'). */
  aliases: string[];
  /** Priorities / pains this persona is measured on. */
  caresAbout: string[];
};

// ── Personas API DTO (snake_case wire) ───────────────────────────────────────
// What the Personas tab + a profile's persona multi-select consume.
export const personaDtoSchema = z.object({
  key: z.string(),
  name: z.string(),
  department: z.string(),
  seniority: z.string(),
  buyer_role: z.string(),
  aliases: z.array(z.string()),
  cares_about: z.array(z.string()),
  // 'seed' (global) | 'custom' (org-authored).
  source: z.string(),
});
export type PersonaDto = z.infer<typeof personaDtoSchema>;

export const personasListResponseSchema = z.object({
  personas: z.array(personaDtoSchema),
});
export type PersonasListResponse = z.infer<typeof personasListResponseSchema>;
